import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Audio } from 'react-loader-spinner';
import { MdOutlineCancel } from "react-icons/md";
import { BsBank } from "react-icons/bs";
import { HiOutlineReceiptTax } from "react-icons/hi";
import { FaRegBuilding } from "react-icons/fa";
import { FaUserCircle } from "react-icons/fa";
import { MdAccountCircle } from "react-icons/md";
import {Layout, CategoryProfile, CategoryConfig} from '../../../../components';
import {handleApi,inputDate} from '../../../../utils/utils';
import AppContext from '../../../../contexts/AppContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const EditCategory = () => {
  const location = useLocation();
  const initialCategory = location.state?.initialCategory;
  
  // FIX: Initialize react-hook-form directly with defaultValues from initialCategory.
  // This removes the need for a separate 'category' state variable that was causing conflicts.
  const { reset, control, register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: initialCategory
  });
  
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [displayImg, setDisplayImg] = useState(''); 
  const [imgType, setImgType] = useState('');
  const { userData, companyProfile, bank, currentColor, actionCount, setActionCount } = useContext(AppContext);

  useEffect(() => {fetchProductCategories();}, []); 
  
  // FIX: This useEffect now only runs once on component mount to handle the initial photo.
  // It is no longer a dependency on 'category' state, which was causing the reset loop.
  useEffect(() => {
    if (initialCategory?.photo_url) {
      fetchImageAsBase64(initialCategory.photo_url);
    } else if (initialCategory?.photo?.data && initialCategory?.photo?.contentType) {
      setDisplayImg(initialCategory.photo.data);
      setImgType(initialCategory.photo.contentType);
    } else {
      setDisplayImg('');
      setImgType('');
    }
  }, [initialCategory]);
  
  // New function to fetch image from URL and convert to base64
  const fetchImageAsBase64 = async (url) => {
    setIsImageLoading(true);
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64data = reader.result.split(',')[1];
        const contentType = blob.type;
        setDisplayImg(base64data);
        setImgType(contentType);
      };
    } catch (error) {
      console.error("Failed to fetch image from URL", error);
      setDisplayImg('');
      setImgType('');
    } finally {
      setIsImageLoading(false);
    }
  };
  
  const fetchProductCategories = async () => {
      const apiEndpoint = 'product_categories';
      const apiMethod = 'GET';
      const parameters = {};
      setIsLoading(true);
      const params = {};
      try {
        const response = await handleApi(apiEndpoint, params, userData, apiMethod, parameters);
        if (response.error) {
          console.error('Error fetching category categories', response.errorMessage);
          return; // Exit function to avoid further processing
        }
        const { status, statusText } = response;
        if (status === 200) {
          const fetchedCategories = response.data; // fetched categories
          //format Category categories  for select options
          const transformedCategories = fetchedCategories.map(category => ({
            value: category.id,
            label: category.name
          }));
      
          // Now update the state with the transformed data
          setCategories(transformedCategories);
        } else {
          console.error(`Error ${status}: ${statusText}`); // Handle non-200 responses
        }
      } catch (error) {
        console.error(error.message || 'An unexpected error occurred.'); // Handle unexpected errors
      } finally {setIsLoading(false);}
    };
  
  
  
  const handleCancel = () => {
    // FIX: reset form and image states to initial values
    reset(initialCategory);
    if (initialCategory?.photo_url) {
        fetchImageAsBase64(initialCategory.photo_url);
    } else {
        setDisplayImg('');
        setImgType('');
    }
  };

  const navigate = useNavigate(); 

  const handleClosePage = () => {
   navigate(-1);
  };

  const convertFile = (files) => {
    if (files && files.length > 0 && files[0] instanceof File) {
      const fileRef = files[0];
      const fileType = fileRef.type;
      const reader = new FileReader();
      reader.readAsBinaryString(fileRef);
      reader.onload = (ev) => {
        const base64Data = btoa(ev.target.result);
        setDisplayImg(base64Data);
        setImgType(fileType);
        // FIX: Remove the setCategory call to prevent the useEffect from triggering a reset.
      };
    }
  };

  const handleImgChange = (event) => {
    const { name, files } = event.target;
    if (name === "logo") {convertFile(files);}
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
  };
  
  const handleCategoryProfileFormSubmit = async (dataFromForm) => {
    try {
    //   console.log('Form data received from react-hook-form (safe stringify):', JSON.stringify(dataFromForm, null, 2));
    } catch (e) {
      console.error("Failed to stringify `dataFromForm`. Circular reference found:", dataFromForm);
      toast.error("Form data is corrupted. Check the console for details.");
      setIsLoading(false);
      return;
    }
 
    const payload = {    // Create payload 
        id: dataFromForm.id,
        name: dataFromForm.name,
        parent_category: dataFromForm.parent_category,
        
      };
      
      // Add the photo data if it exists
    if (displayImg && imgType) {
        payload.photo = {data: displayImg,contentType: imgType};
    } else { payload.photo = null;}
      
    try {
      console.log('Final payload to send to API (safe stringify):', JSON.stringify(payload, null, 2));
    } catch (e) {
      console.error("Final payload has a circular reference. Inspect the object below.", payload);
      toast.error("The final payload is corrupted. Check the console for details.");
      setIsLoading(false);
      return;
    }

    const apiMethod = payload.id ? 'PUT' : 'POST';
    const parameters = { ...(payload.id && { id: payload.id }) };
    setIsLoading(true);
    try {
      const response = await handleApi('product_categories', payload, userData, apiMethod, parameters);
      if (response.status === 200 || response.status === 201) {
        toast.success('Category profile saved successfully!');
        // FIX: The component will now display the updated data without a full reset.
        // The parent state no longer needs to be explicitly updated here.
      } else {
        toast.error(`Error saving category: ${response.statusText || 'An unknown error occurred'}`);
      }
    } catch (error) {
      toast.error('Error saving category: ' + error.message);
    } finally {
      setActionCount(actionCount + 1);
      setIsLoading(false);
    }
  };
  const svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 512 512" fill="${currentColor}"><path d="M256,0C114.6,0,0,114.6,0,256s114.6,256,256,256s256-114.6,256-256S397.4,0,256,0z M256,79.5c37.5,0,67.9,30.4,67.9,67.9S293.5,215.4,256,215.4S188.1,185,188.1,147.4S218.5,79.5,256,79.5z M256,441.6c-70.5,0-134.4-29.3-177.3-76.3c-0.3-0.3-0.5-0.6-0.8-1c-0-0-0-0-0-0.1C72,322,112.5,274.6,161.4,274.6h189.1c48.9,0,89.4,47.4,83.6,90.5c-0,0-0,0-0.1,0.1c-0.3,0.3-0.5,0.7-0.8,1C390.4,412.3,326.5,441.6,256,441.6z" /></svg>`;
  const base64Svg = btoa(unescape(encodeURIComponent(svgString)));
  const defaultPhotoUrl = `data:image/svg+xml;base64,${base64Svg}`;

  const photoUrl = isImageLoading ? null : (displayImg && imgType ? `data:${imgType};base64,${displayImg}` : defaultPhotoUrl);
  return (
    <Layout>
      <div className="m-1 sm:m-2">
        <div className="relative flex justify-between items-center">
          <h1 className="text-center font-bold flex-grow">EDIT PRODUCT CATEGORY</h1>
          <button
            onClick={handleClosePage}
            className="p-1 rounded-lg text-red-400 bg-white hover:bg-gray-50 hover:text-gray-600"
          >
            <MdOutlineCancel color={currentColor} size={30} />
          </button>
        </div>
        
        <ToastContainer position="top-center" autoClose={2000} />

        {isLoading ? (
          <div className="flex items-center flex-col py-6">
            <Audio height="80" width="80" radius="9" color={currentColor} ariaLabel="loading" />
            <p className="mt-2 text-white">Loading. Please wait...</p>
          </div>
        ) : (
          <div className="bg-white p-2 sm:p-4 my-4 my-auto mx-auto max-w-[80%] rounded-xl shadow-2xl">
            <div className="mx-auto border-b border-gray-400 bg-gray-500 transition-all duration-200 cursor-pointer rounded-xl shadow-2xl p-8">
              <CategoryProfile
                  fields={CategoryConfig}
                  control={control}
                  register={register}
                  photo={photoUrl}
                  onChange={handleChange}
                  categories={categories}
                  handleImgChange={handleImgChange}
                  onSubmit={handleSubmit(handleCategoryProfileFormSubmit)}
                  categoryData={initialCategory} // FIX: Pass initialCategory here instead of a mutable state variable.
                  onCancel={handleCancel}
                  errors={errors}
                  reset={reset}
                  isLoading={isLoading}
                />
              </div>
            
          </div>
        )}
      </div>
    </Layout>
  );
};
export default EditCategory;
